import { JOB_STATUS, type JobStatus } from '@/types'

export type AgendaDragPayload =
  | {
      type: 'unassigned-job'
      jobId: string
      durationMinutes: number
      grabOffsetY: number
      startTime?: string | null
      endTime?: string | null
    }
  | {
      type: 'scheduled-job'
      jobId: string
      fromTechnicianId: string
      technicianIds: string[]
      durationMinutes: number
      status: JobStatus
      grabOffsetY: number
      startTime?: string | null
      endTime?: string | null
    }
  | { type: 'technician-column'; technicianId: string; fromIndex: number }

export const AGENDA_DRAG_MIME = 'application/x-tigerflow-agenda-drag'

let currentAgendaDragPayload: AgendaDragPayload | null = null
let currentAgendaDragPreview: {
  root: HTMLElement
  label: HTMLElement
  offsetX: number
  offsetY: number
} | null = null
let transparentDragImage: HTMLElement | null = null
let lastPreviewLabel = ''

const JOB_STATUS_VALUES: readonly JobStatus[] = Object.values(JOB_STATUS)

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}

function isString(value: unknown): value is string {
  return typeof value === 'string' && value.length > 0
}

function isNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value)
}

function isOptionalTime(value: unknown): value is string | null | undefined {
  return value === undefined || value === null || typeof value === 'string'
}

function isJobStatus(value: unknown): value is JobStatus {
  return JOB_STATUS_VALUES.includes(value as JobStatus)
}

function isAgendaDragPayload(value: unknown): value is AgendaDragPayload {
  if (!isRecord(value) || typeof value.type !== 'string') return false

  if (value.type === 'unassigned-job') {
    return (
      isString(value.jobId) &&
      isNumber(value.durationMinutes) &&
      isNumber(value.grabOffsetY) &&
      isOptionalTime(value.startTime) &&
      isOptionalTime(value.endTime)
    )
  }

  if (value.type === 'scheduled-job') {
    return (
      isString(value.jobId) &&
      isString(value.fromTechnicianId) &&
      Array.isArray(value.technicianIds) &&
      value.technicianIds.every(isString) &&
      isNumber(value.durationMinutes) &&
      isJobStatus(value.status) &&
      isNumber(value.grabOffsetY) &&
      isOptionalTime(value.startTime) &&
      isOptionalTime(value.endTime)
    )
  }

  if (value.type === 'technician-column') {
    return isString(value.technicianId) && isNumber(value.fromIndex)
  }

  return false
}

function dragLabel(payload: AgendaDragPayload): string {
  return payload.type === 'technician-column'
    ? payload.technicianId
    : payload.jobId
}

export function setAgendaDragPayload(
  dataTransfer: DataTransfer,
  payload: AgendaDragPayload
): void {
  currentAgendaDragPayload = payload
  dataTransfer.setData(AGENDA_DRAG_MIME, JSON.stringify(payload))
  dataTransfer.setData('text/plain', dragLabel(payload))
}

export function setAgendaDragPreview(
  dataTransfer: DataTransfer,
  source: HTMLElement,
  clientX: number,
  clientY: number
): void {
  clearAgendaDragPreview()

  const rect = source.getBoundingClientRect()
  const offsetX = Math.max(0, Math.min(rect.width, clientX - rect.left))
  const offsetY = Math.max(0, Math.min(rect.height, clientY - rect.top))

  const root = document.createElement('div')
  root.setAttribute('aria-hidden', 'true')
  root.style.position = 'fixed'
  root.style.left = '0'
  root.style.top = '0'
  root.style.width = `${rect.width}px`
  root.style.height = `${rect.height}px`
  root.style.pointerEvents = 'none'
  root.style.zIndex = '2147483647'
  root.style.transformOrigin = 'top left'
  root.style.willChange = 'transform'

  const label = document.createElement('div')
  label.style.position = 'absolute'
  label.style.left = '50%'
  label.style.bottom = `calc(100% + 8px)`
  label.style.transform = 'translateX(-50%)'
  label.style.display = 'none'
  label.style.maxWidth = '260px'
  label.style.whiteSpace = 'nowrap'
  label.style.overflow = 'hidden'
  label.style.textOverflow = 'ellipsis'
  label.style.borderRadius = '8px'
  label.style.backgroundColor = '#111827'
  label.style.padding = '6px 10px'
  label.style.fontFamily = 'Inter, ui-sans-serif, system-ui, sans-serif'
  label.style.fontSize = '13px'
  label.style.fontWeight = '700'
  label.style.lineHeight = '16px'
  label.style.color = '#ffffff'
  label.style.boxShadow = '0 8px 16px -8px rgba(17, 24, 39, 0.6)'

  const card = source.cloneNode(true) as HTMLElement
  card.style.position = 'relative'
  card.style.left = '0'
  card.style.top = '0'
  card.style.width = `${rect.width}px`
  card.style.height = `${rect.height}px`
  card.style.opacity = '1'
  card.style.transform = 'none'
  card.style.boxShadow =
    '0 18px 38px -18px rgba(17, 24, 39, 0.48), 0 8px 18px -10px rgba(17, 24, 39, 0.28)'

  root.append(label, card)
  document.body.appendChild(root)
  currentAgendaDragPreview = { root, label, offsetX, offsetY }
  updateAgendaDragPreview(clientX, clientY)

  transparentDragImage = document.createElement('div')
  transparentDragImage.style.width = '1px'
  transparentDragImage.style.height = '1px'
  transparentDragImage.style.opacity = '0'
  document.body.appendChild(transparentDragImage)
  dataTransfer.setDragImage(transparentDragImage, 0, 0)
  document.addEventListener('dragover', handleDocumentDragOver, true)
}

export function updateAgendaDragPreview(
  clientX: number,
  clientY: number,
  label?: string | null
): void {
  if (!currentAgendaDragPreview) return

  if (label !== undefined) lastPreviewLabel = label ?? ''
  const {
    root,
    label: labelElement,
    offsetX,
    offsetY,
  } = currentAgendaDragPreview
  root.style.transform = `translate3d(${Math.round(clientX - offsetX)}px, ${Math.round(clientY - offsetY)}px, 0) scale(1.02)`
  labelElement.textContent = lastPreviewLabel
  labelElement.style.display = lastPreviewLabel ? 'block' : 'none'
}

export function getAgendaDragPayload(
  dataTransfer: DataTransfer
): AgendaDragPayload | null {
  const rawPayload = dataTransfer.getData(AGENDA_DRAG_MIME)
  if (!rawPayload) return currentAgendaDragPayload

  try {
    const payload: unknown = JSON.parse(rawPayload)
    return isAgendaDragPayload(payload) ? payload : null
  } catch {
    return currentAgendaDragPayload
  }
}

export function getCurrentAgendaDragPayload(): AgendaDragPayload | null {
  return currentAgendaDragPayload
}

export function clearAgendaDragPayload(): void {
  currentAgendaDragPayload = null
  clearAgendaDragPreview()
}

function handleDocumentDragOver(event: DragEvent): void {
  updateAgendaDragPreview(event.clientX, event.clientY)
}

function clearAgendaDragPreview(): void {
  document.removeEventListener('dragover', handleDocumentDragOver, true)
  currentAgendaDragPreview?.root.remove()
  currentAgendaDragPreview = null
  transparentDragImage?.remove()
  transparentDragImage = null
  lastPreviewLabel = ''
}
